import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Alert, Card, CardBody } from 'reactstrap';
import { loadStripe } from '@stripe/stripe-js';
import { useLocation } from 'react-router-dom';

// Initialize Stripe (replace with your publishable key)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...');

interface PaymentReleaseModalProps {
    isOpen: boolean;
    toggle: () => void;
    milestoneId: string;
    milestoneTitle: string;
    paymentAmount: number;
    contractId: string;
    onPaymentSuccess: () => void;
}

const PaymentReleaseModal: React.FC<PaymentReleaseModalProps> = ({
    isOpen,
    toggle,
    milestoneId,
    milestoneTitle,
    paymentAmount,
    contractId,
    onPaymentSuccess
}) => {
    const [accountHolderName, setAccountHolderName] = useState('');
    const [accountHolderType, setAccountHolderType] = useState<'individual' | 'company'>('individual');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'succeeded' | 'failed' | 'connecting_bank'>('idle');
    const location = useLocation();

    // Check for payment success on component mount and when URL changes
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const paymentSuccess = urlParams.get('payment_success');
        const paymentSessionId = urlParams.get('session_id');
        
        if (paymentSuccess === 'true' && paymentSessionId) {
            setPaymentStatus('succeeded');
            onPaymentSuccess();
            
            // Clean up URL parameters
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            
            // Show success message for a moment then close
            setTimeout(() => {
                resetForm();
                toggle();
            }, 2000);
        }
    }, [location.search, onPaymentSuccess, toggle]);

    const resetForm = () => {
        setAccountHolderName('');
        setAccountHolderType('individual');
        setError('');
        setShowConfirmation(false);
        setIsProcessing(false);
        setPaymentStatus('idle');
    };

    const validateForm = () => {
        if (!accountHolderName.trim()) {
            setError('Account holder name is required');
            return false;
        }
        return true;
    };

    const handleFinancialConnections = async (clientSecret: string, paymentData: any) => {
        try {
            setPaymentStatus('connecting_bank');
            const stripe = await stripePromise;
            
            if (!stripe) {
                throw new Error('Stripe failed to load');
            }

            console.log('Starting Financial Connections flow with client secret:', clientSecret);

            // Use type assertion to handle the missing TypeScript definition
            const stripeWithFC = stripe as any;
            const { financialConnectionsSession, error: fcError } = await stripeWithFC.collectFinancialConnectionsAccounts({
                clientSecret: clientSecret,
                expand: ['ownership']
            });

            if (fcError) {
                console.error('Financial Connections error:', fcError);
                throw new Error(fcError.message);
            }

            console.log('Financial Connections session result:', financialConnectionsSession);

            // Fixed: Check if financialConnectionsSession exists and has accounts
            if (!financialConnectionsSession) {
                console.error('financialConnectionsSession is undefined');
                throw new Error('Financial Connections session is undefined');
            }

            // More flexible check for accounts - try different possible structures
            let hasAccounts = false;
            let accountCount = 0;

            // Check if accounts is directly an array (which it is in this case)
            if (Array.isArray(financialConnectionsSession.accounts) && financialConnectionsSession.accounts.length > 0) {
                hasAccounts = true;
                accountCount = financialConnectionsSession.accounts.length;
                console.log('Found accounts as direct array:', accountCount);
            }
            // Check standard structure (accounts.data)
            else if (financialConnectionsSession.accounts?.data?.length > 0) {
                hasAccounts = true;
                accountCount = financialConnectionsSession.accounts.data.length;
                console.log('Found accounts in data property:', accountCount);
            }
            // Check if there's a different accounts structure
            else if (financialConnectionsSession.linked_accounts?.length > 0) {
                hasAccounts = true;
                accountCount = financialConnectionsSession.linked_accounts.length;
                console.log('Found accounts in linked_accounts:', accountCount);
            }

            if (!hasAccounts) {
                console.error('No accounts found in session. Full session object keys:', Object.keys(financialConnectionsSession));
                throw new Error('No bank account was connected. Please try again.');
            }

            console.log('Bank account connected successfully:', accountCount, 'accounts');

            // Complete the payment on the server
            const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
            console.log('Sending completion request to:', `${serverUrl}/complete-ach-payment`);
            
            const completeResponse = await fetch(`${serverUrl}/complete-ach-payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    financialConnectionsSessionId: financialConnectionsSession.id,
                    ...paymentData
                }),
            });

            console.log('Server response status:', completeResponse.status);

            if (!completeResponse.ok) {
                const errorText = await completeResponse.text();
                console.error('Server response error:', errorText);
                throw new Error(`HTTP error! status: ${completeResponse.status} - ${errorText}`);
            }

            const result = await completeResponse.json();
            console.log('Payment completion result:', result);

            if (result.success) {
                setPaymentStatus('succeeded');
                
                // Call onPaymentSuccess which should refresh the contract data
                onPaymentSuccess();
                
                // Give the server a moment to process and then refresh the page data
                setTimeout(() => {
                    // Force a page refresh to ensure we get the latest contract state
                    window.location.reload();
                }, 2000);
            } else {
                throw new Error(result.message || 'Payment completion failed');
            }

        } catch (error: unknown) {
            console.error('Financial Connections error:', error);
            setPaymentStatus('failed');
            
            if (error instanceof Error) {
                setError(error.message);
            } else {
                setError('Failed to connect bank account. Please try again.');
            }
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsProcessing(true);
        setError('');
        setPaymentStatus('processing');

        try {
            const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
            const apiUrl = `${serverUrl}/process-ach-payment`;

            // For development, use localhost HTTPS or production URL
            const isLocalhost = window.location.hostname === 'localhost';
            const returnUrl = isLocalhost 
                ? `https://www.bidbro.com/contract/${contractId}?payment_success=true&contract_id=${contractId}&milestone_id=${milestoneId}`
                : `${window.location.origin}/contract/${contractId}?payment_success=true&contract_id=${contractId}&milestone_id=${milestoneId}`;

            console.log('Submitting payment with return URL:', returnUrl);

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountHolderName,
                    accountHolderType,
                    amount: Math.round(paymentAmount * 100), // Convert to cents
                    milestoneId,
                    contractId,
                    returnUrl
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Payment initiation failed:', errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            console.log('Payment initiation result:', result);

            if (result.success) {
                if (result.requiresFinancialConnections) {
                    console.log('Starting Financial Connections flow');
                    // Handle Financial Connections flow
                    await handleFinancialConnections(
                        result.financialConnectionsSession.client_secret,
                        result.paymentData
                    );
                } else if (result.paymentIntent?.status === 'succeeded') {
                    setPaymentStatus('succeeded');
                    onPaymentSuccess();
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                } else if (result.paymentIntent?.status === 'processing' || 
                           result.paymentIntent?.status === 'requires_action') {
                    setPaymentStatus('processing');
                    setError('');
                    onPaymentSuccess(); // Mark as successful since payment is initiated
                    setTimeout(() => {
                        window.location.reload();
                    }, 3000);
                } else {
                    setPaymentStatus('failed');
                    setError(result.message || 'Payment processing failed');
                }
            } else {
                setPaymentStatus('failed');
                setError(result.message || 'Payment processing failed');
            }
        } catch (error: unknown) {
            console.error('Payment processing error:', error);
            setPaymentStatus('failed');

            if (error instanceof Error) {
                if (error.name === 'TypeError' && error.message.includes('fetch')) {
                    setError('Unable to connect to payment server. Please check your connection and try again.');
                } else if (error.message.includes('HTTP error')) {
                    setError(`Server error: ${error.message}`);
                } else {
                    setError(error.message || 'Network error. Please try again.');
                }
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const handleClose = () => {
        if (!isProcessing && paymentStatus !== 'connecting_bank') {
            resetForm();
            toggle();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg" backdrop="static">
            <ModalHeader toggle={handleClose}>
                Make Milestone Payment
            </ModalHeader>
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}

                {!showConfirmation ? (
                    <>
                        <Card className="mb-4">
                            <CardBody>
                                <h5>Payment Details</h5>
                                <p><strong>Milestone:</strong> {milestoneTitle}</p>
                                <p><strong>Amount:</strong> {formatCurrency(paymentAmount)}</p>
                                <Alert color="info">
                                    <small>
                                        <strong>Note:</strong> These project funds will be held securely until you confirm the work is complete and meets your contract requirements.
                                    </small>
                                </Alert>
                            </CardBody>
                        </Card>

                        <h5 className="mb-3">Account Holder Information</h5>

                        <FormGroup>
                            <Label for="accountHolderName">Account Holder Name <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="accountHolderName"
                                value={accountHolderName}
                                onChange={(e) => setAccountHolderName(e.target.value)}
                                placeholder="Enter full name as it appears on the account"
                                disabled={isProcessing}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="accountHolderType">Account Type <span className="text-danger">*</span></Label>
                            <Input
                                type="select"
                                id="accountHolderType"
                                value={accountHolderType}
                                onChange={(e) => setAccountHolderType(e.target.value as 'individual' | 'company')}
                                disabled={isProcessing}
                            >
                                <option value="individual">Individual</option>
                                <option value="company">Company</option>
                            </Input>
                        </FormGroup>

                        <Alert color="success">
                            <small>
                                <strong>🔒 Secure Bank Connection:</strong> We use Stripe Financial Connections 
                                to securely verify your bank account. This provides instant verification and 
                                enhanced security without storing your banking credentials.
                            </small>
                        </Alert>
                    </>
                ) : (
                    <div className="text-center">
                        {paymentStatus === 'connecting_bank' && (
                            <div>
                                <h4 className="text-info">Connecting Bank Account...</h4>
                                <Alert color="info">
                                    <strong>Please complete the bank connection process</strong><br />
                                    A secure popup has opened to connect your bank account through Stripe Financial Connections.
                                    Once connected, your payment will be processed automatically.
                                </Alert>
                            </div>
                        )}
                        {paymentStatus === 'processing' && (
                            <div>
                                <h4 className="text-info">Processing Payment...</h4>
                                <Alert color="info">
                                    <strong>ACH Payment Initiated</strong><br />
                                    Your payment is being processed. ACH transfers typically take 1-3 business days to complete.
                                    The milestone will be marked as completed and you'll receive confirmation once the transfer is finalized.
                                </Alert>
                            </div>
                        )}
                        {paymentStatus === 'succeeded' && (
                            <div>
                                <h4 className="text-success">Payment Successful!</h4>
                                <Alert color="success">
                                    <strong>Payment processed successfully!</strong><br />
                                    The milestone "{milestoneTitle}" has been marked as paid. Your ACH payment is being processed 
                                    and will complete within 1-3 business days.
                                </Alert>
                            </div>
                        )}
                        {paymentStatus === 'idle' && (
                            <div>
                                <h4>Confirm Deposit Payment</h4>
                                <Card>
                                    <CardBody>
                                        <p><strong>Milestone:</strong> {milestoneTitle}</p>
                                        <p><strong>Amount:</strong> {formatCurrency(paymentAmount)}</p>
                                        <p><strong>Account Holder:</strong> {accountHolderName}</p>
                                        <p><strong>Method:</strong> Stripe Financial Connections</p>
                                    </CardBody>
                                </Card>
                                <Alert color="warning" className="mt-3">
                                    <strong>Important:</strong> You'll be prompted to securely connect your bank account.
                                    ACH payments typically process within 1-3 business days.
                                    Once initiated, this payment cannot be reversed. Please verify all information is correct.
                                </Alert>
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {!showConfirmation ? (
                    <>
                        <Button color="secondary" onClick={handleClose} disabled={isProcessing}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                if (validateForm()) {
                                    setShowConfirmation(true);
                                }
                            }}
                            disabled={isProcessing || !accountHolderName}
                        >
                            Connect Bank & Pay
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            color="secondary"
                            onClick={() => setShowConfirmation(false)}
                            disabled={isProcessing || paymentStatus === 'processing' || paymentStatus === 'succeeded' || paymentStatus === 'connecting_bank'}
                        >
                            Back
                        </Button>
                        {paymentStatus === 'idle' && (
                            <Button
                                color="success"
                                onClick={handleSubmit}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Connecting...' : `Pay ${formatCurrency(paymentAmount)}`}
                            </Button>
                        )}
                        {(paymentStatus === 'processing' || paymentStatus === 'succeeded') && (
                            <Button
                                color="primary"
                                onClick={() => {
                                    resetForm();
                                    toggle();
                                }}
                            >
                                Close
                            </Button>
                        )}
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default PaymentReleaseModal;